import { useEffect, useMemo, useState } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import {
    FaDownload,
    FaFileExcel,
    FaSpinner,
    FaPlus,
    FaTimes,
    FaBalanceScale,
    FaRegClock,
    FaRegCalendarAlt,
} from "react-icons/fa";

const hoyISO = () => new Date().toISOString().slice(0, 10);
const num = (v) => Number(v || 0).toLocaleString("es-MX");
const bucketHora = (b) =>
    `${String(Math.floor(b / 2)).padStart(2, "0")}:${b % 2 ? "30" : "00"}`;

// Paleta estable por clave de result (para que el mismo result tenga el mismo color).
const COLORES = [
    "#0891b2", "#7c3aed", "#db2777", "#ea580c", "#16a34a",
    "#2563eb", "#ca8a04", "#dc2626", "#0d9488", "#9333ea",
    "#e11d48", "#65a30d", "#0284c7", "#c026d3", "#f59e0b",
];
const colorDeClave = (clave, idx) => COLORES[idx % COLORES.length];

export default function TraficoResult() {
    const [tab, setTab] = useState("descargar");

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    <FaFileExcel className="text-emerald-600" />
                    Tráfico por result
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Reporte por media hora (48 franjas), con conteo y % por cada result.
                </p>
            </div>

            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 mb-5">
                {[
                    ["descargar", "Descargar", <FaDownload key="d" />],
                    ["comparar", "Comparar", <FaBalanceScale key="c" />],
                ].map(([val, label, icon]) => (
                    <button
                        key={val}
                        onClick={() => setTab(val)}
                        className={`px-4 py-1.5 text-sm rounded-md inline-flex items-center gap-2 ${tab === val
                                ? "bg-cyan-600 text-white"
                                : "text-slate-600 hover:bg-slate-50"
                            }`}
                    >
                        {icon} {label}
                    </button>
                ))}
            </div>

            {tab === "descargar" ? <Descargar /> : <Comparar />}
        </div>
    );
}

/* ─────────────────────────── PESTAÑA DESCARGAR ─────────────────────────── */
function Descargar() {
    const [modo, setModo] = useState("dia");
    const [fecha, setFecha] = useState(hoyISO());
    const [mes, setMes] = useState(hoyISO().slice(0, 7));
    const [desde, setDesde] = useState(hoyISO());
    const [hasta, setHasta] = useState(hoyISO());
    const [mesesDisp, setMesesDisp] = useState([]);
    const [bajando, setBajando] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios
            .post(API_ROUTES.trafico.meses, {})
            .then((res) => setMesesDisp(res.data?.meses || []))
            .catch(() => setMesesDisp([]));
    }, []);

    const descargar = async () => {
        setError(null);
        setBajando(true);
        try {
            const body =
                modo === "dia"
                    ? { modo, fecha }
                    : modo === "mes"
                        ? { modo, mes }
                        : { modo, desde, hasta };
            const res = await axios.post(API_ROUTES.trafico.excel, body, {
                responseType: "blob",
                timeout: 120000,
            });
            const cd = res.headers?.["content-disposition"] || "";
            const m = /filename="?([^"]+)"?/.exec(cd);
            const nombre =
                m?.[1] ||
                (modo === "dia"
                    ? `trafico_clearcom_${fecha}.xlsx`
                    : modo === "mes"
                        ? `trafico_clearcom_${mes}.xlsx`
                        : `trafico_clearcom_${desde}_a_${hasta}.xlsx`);
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            let msg = err?.message || "Error al generar el reporte";
            if (err?.response?.data instanceof Blob) {
                try {
                    msg = JSON.parse(await err.response.data.text())?.message || msg;
                } catch {
                    /* deja msg */
                }
            } else if (err?.code === "ECONNABORTED") {
                msg = "La generación tardó demasiado. Intenta un rango más corto.";
            }
            setError(msg);
        } finally {
            setBajando(false);
        }
    };

    return (
        <>
            <p className="text-sm text-slate-500 mb-4">
                Un día es una hoja; mes y rango traen una hoja por día más la hoja
                Resumen.
            </p>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 mb-5">
                {[
                    ["dia", "Por día"],
                    ["mes", "Por mes"],
                    ["rango", "Por rango"],
                ].map(([val, label]) => (
                    <button
                        key={val}
                        onClick={() => setModo(val)}
                        className={`px-4 py-1.5 text-sm rounded-md ${modo === val
                                ? "bg-cyan-600 text-white"
                                : "text-slate-600 hover:bg-slate-50"
                            }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 mb-5">
                {modo === "dia" && (
                    <Campo label="Día">
                        <input type="date" value={fecha} max={hoyISO()}
                            onChange={(e) => setFecha(e.target.value)} className="input-fecha" />
                    </Campo>
                )}
                {modo === "mes" && (
                    <Campo label="Mes">
                        <input type="month" value={mes} max={hoyISO().slice(0, 7)}
                            onChange={(e) => setMes(e.target.value)} className="input-fecha" />
                        {mesesDisp.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {mesesDisp.map((mm) => (
                                    <button key={mm} onClick={() => setMes(mm)}
                                        className={`text-xs px-2 py-1 rounded border ${mes === mm
                                                ? "bg-cyan-50 border-cyan-300 text-cyan-700"
                                                : "border-slate-200 text-slate-500 hover:bg-slate-50"
                                            }`}>{mm}</button>
                                ))}
                            </div>
                        )}
                    </Campo>
                )}
                {modo === "rango" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Campo label="Desde">
                            <input type="date" value={desde} max={hoyISO()}
                                onChange={(e) => setDesde(e.target.value)} className="input-fecha" />
                        </Campo>
                        <Campo label="Hasta">
                            <input type="date" value={hasta} max={hoyISO()}
                                onChange={(e) => setHasta(e.target.value)} className="input-fecha" />
                        </Campo>
                    </div>
                )}
            </div>

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    {error}
                </div>
            )}

            <button onClick={descargar} disabled={bajando}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
                {bajando ? (<><FaSpinner className="animate-spin" /> Generando…</>) : (<><FaDownload /> Descargar Excel</>)}
            </button>
            <EstiloInput />
        </>
    );
}

/* ─────────────────────────── PESTAÑA COMPARAR ─────────────────────────── */
function Comparar() {
    const [gran, setGran] = useState("30"); // '30' | '60' | 'dia'
    const [cols, setCols] = useState([
        { id: 1, fecha: hoyISO(), valor: 0 },
        { id: 2, fecha: hoyISO(), valor: 0 },
    ]);
    const [nextId, setNextId] = useState(3);
    const [data, setData] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [bajando, setBajando] = useState(false);
    const [error, setError] = useState(null);
    // guarda las selecciones con las que se generó la tabla actual (para descargar lo mismo)
    const [selActual, setSelActual] = useState(null);

    const esDia = gran === "dia";

    const opts = useMemo(() => {
        if (gran === "60") {
            return Array.from({ length: 24 }, (_, h) => ({
                value: h, label: `${String(h).padStart(2, "0")}:00`,
            }));
        }
        return Array.from({ length: 48 }, (_, b) => ({ value: b, label: bucketHora(b) }));
    }, [gran]);

    const cambiarGran = (g) => {
        if (gran === g) return;
        setCols((cs) =>
            cs.map((c) => {
                let valor = c.valor;
                // remapeo entre 30 y 60; dia no usa valor
                if (gran === "30" && g === "60") valor = Math.floor(c.valor / 2);
                else if (gran === "60" && g === "30") valor = c.valor * 2;
                return { ...c, valor };
            })
        );
        setGran(g);
        setData(null);
    };

    const toBuckets = (valor) =>
        esDia
            ? { bIni: 0, bFin: 47 }
            : gran === "60"
                ? { bIni: valor * 2, bFin: valor * 2 + 1 }
                : { bIni: valor, bFin: valor };

    const addCol = () => {
        if (cols.length >= 8) return;
        setCols((cs) => [...cs, { id: nextId, fecha: hoyISO(), valor: 0 }]);
        setNextId((n) => n + 1);
    };
    const rmCol = (id) => setCols((cs) => cs.filter((c) => c.id !== id));
    const setCol = (id, patch) =>
        setCols((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));

    const comparar = async () => {
        setError(null);
        setCargando(true);
        try {
            const selecciones = cols.map((c) => ({ fecha: c.fecha, ...toBuckets(c.valor) }));
            const res = await axios.post(API_ROUTES.trafico.comparar, { selecciones });
            setData(res.data);
            setSelActual(selecciones);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Error");
            setData(null);
        } finally {
            setCargando(false);
        }
    };

    const descargar = async () => {
        if (!selActual) return;
        setBajando(true);
        try {
            const res = await axios.post(
                API_ROUTES.trafico.compararExcel,
                { selecciones: selActual },
                { responseType: "blob", timeout: 120000 }
            );
            const cd = res.headers?.["content-disposition"] || "";
            const m = /filename="?([^"]+)"?/.exec(cd);
            const nombre = m?.[1] || `trafico_comparacion_${selActual[0]?.fecha || ""}.xlsx`;
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            let msg = err?.message || "Error al descargar";
            if (err?.response?.data instanceof Blob) {
                try {
                    msg = JSON.parse(await err.response.data.text())?.message || msg;
                } catch {
                    /* deja msg */
                }
            }
            setError(msg);
        } finally {
            setBajando(false);
        }
    };

    const etiquetaCol = (c) =>
        c.bIni === 0 && c.bFin === 47
            ? { fecha: c.fecha, franja: "Día completo" }
            : {
                fecha: c.fecha,
                franja: `${bucketHora(c.bIni)}${c.bIni !== c.bFin ? " · 1h" : ""}`,
            };

    // maximo por columna (para la barra de intensidad relativa)
    const maxPorCol = useMemo(() => {
        if (!data) return [];
        return data.columnas.map((c) =>
            Math.max(1, ...data.filas.map((f) => c.porClave?.[f.clave] || 0))
        );
    }, [data]);

    return (
        <>
            <p className="text-sm text-slate-500 mb-4">
                Elige franjas (día + hora) y compáralas lado a lado. Cada columna es una
                selección; cada fila, un result.
            </p>

            {/* granularidad */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs text-slate-500">Granularidad:</span>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                    {[
                        ["30", "30 min", <FaRegClock key="a" className="text-xs" />],
                        ["60", "1 hora", <FaRegClock key="b" className="text-xs" />],
                        ["dia", "Día completo", <FaRegCalendarAlt key="c" className="text-xs" />],
                    ].map(([v, l, ic]) => (
                        <button key={v} onClick={() => cambiarGran(v)}
                            className={`px-3 py-1 text-sm rounded-md inline-flex items-center gap-1.5 ${gran === v ? "bg-cyan-600 text-white" : "text-slate-600 hover:bg-slate-50"
                                }`}>{ic} {l}</button>
                    ))}
                </div>
            </div>

            {/* selectores de columnas */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-5 shadow-sm">
                <div className="flex flex-wrap gap-3">
                    {cols.map((c, i) => (
                        <div key={c.id}
                            className="relative flex flex-col gap-2 rounded-xl border border-slate-200 p-3 bg-gradient-to-b from-slate-50 to-white min-w-[190px]">
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                    Columna {i + 1}
                                </span>
                                {cols.length > 2 && (
                                    <button onClick={() => rmCol(c.id)}
                                        className="h-6 w-6 flex items-center justify-center rounded-md text-slate-300 hover:bg-red-50 hover:text-red-500"
                                        title="Quitar">
                                        <FaTimes className="text-xs" />
                                    </button>
                                )}
                            </div>
                            <input type="date" value={c.fecha} max={hoyISO()}
                                onChange={(e) => setCol(c.id, { fecha: e.target.value })}
                                className="input-fecha" />
                            {!esDia && (
                                <select value={c.valor}
                                    onChange={(e) => setCol(c.id, { valor: Number(e.target.value) })}
                                    className="input-fecha">
                                    {opts.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            )}
                            {esDia && (
                                <div className="text-xs text-slate-400 px-1 py-2 inline-flex items-center gap-1">
                                    <FaRegCalendarAlt /> 00:00 – 23:59
                                </div>
                            )}
                        </div>
                    ))}
                    {cols.length < 8 && (
                        <button onClick={addCol}
                            className="min-w-[140px] flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-cyan-300 hover:text-cyan-600 hover:bg-cyan-50/40 transition-colors">
                            <FaPlus /> <span className="text-xs">Agregar</span>
                        </button>
                    )}
                </div>

                <button onClick={comparar} disabled={cargando}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-60 shadow-sm">
                    {cargando ? (<><FaSpinner className="animate-spin" /> Comparando…</>) : (<><FaBalanceScale /> Comparar</>)}
                </button>
            </div>

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    {error}
                </div>
            )}

            {/* resultado */}
            {data && data.columnas?.length > 0 && (
                <>
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-slate-500">
                            {data.filas.length} results · {data.columnas.length} franjas
                        </span>
                        <button onClick={descargar} disabled={bajando}
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
                            {bajando ? (<><FaSpinner className="animate-spin" /> Generando…</>) : (<><FaDownload /> Descargar comparación</>)}
                        </button>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        {/* encabezado de columnas tipo "tarjetas" */}
                        <div
                            className="grid border-b border-slate-100 bg-slate-50/60"
                            style={{ gridTemplateColumns: `minmax(220px,1.4fr) repeat(${data.columnas.length}, 1fr)` }}
                        >
                            <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                result
                            </div>
                            {data.columnas.map((c, i) => {
                                const et = etiquetaCol(c);
                                return (
                                    <div key={i} className="px-4 py-3 text-right">
                                        <div className="text-sm font-semibold text-slate-700">{et.fecha}</div>
                                        <div className="text-xs text-cyan-600">{et.franja}</div>
                                        <div className="text-[11px] text-slate-400 mt-0.5">{num(c.total)} llam.</div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* filas con barra de intensidad */}
                        <div>
                            {data.filas.map((f, fi) => {
                                const color = colorDeClave(f.clave, fi);
                                return (
                                    <div key={f.clave}
                                        className="grid items-center border-b border-slate-50 hover:bg-slate-50/50"
                                        style={{ gridTemplateColumns: `minmax(220px,1.4fr) repeat(${data.columnas.length}, 1fr)` }}
                                    >
                                        <div className="px-4 py-2.5 flex items-center gap-2">
                                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
                                            <span className="text-sm text-slate-700">{f.alias}</span>
                                        </div>
                                        {data.columnas.map((c, i) => {
                                            const n = c.porClave?.[f.clave] || 0;
                                            const pct = c.total ? Math.round((1000 * n) / c.total) / 10 : 0;
                                            const rel = n / maxPorCol[i]; // intensidad relativa dentro de la columna
                                            return (
                                                <div key={i} className="px-4 py-2.5">
                                                    {n ? (
                                                        <div className="flex flex-col items-end gap-1">
                                                            <div className="text-sm tabular-nums text-slate-700">
                                                                {num(n)}{" "}
                                                                <span className="text-slate-400 text-xs">({pct}%)</span>
                                                            </div>
                                                            <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                                <div className="h-full rounded-full"
                                                                    style={{ width: `${Math.max(3, rel * 100)}%`, background: color, opacity: 0.75 }} />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-right text-slate-300 text-sm">—</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>

                        {/* total */}
                        <div
                            className="grid items-center bg-slate-50 font-medium"
                            style={{ gridTemplateColumns: `minmax(220px,1.4fr) repeat(${data.columnas.length}, 1fr)` }}
                        >
                            <div className="px-4 py-3 text-slate-700">Total</div>
                            {data.columnas.map((c, i) => (
                                <div key={i} className="px-4 py-3 text-right tabular-nums text-slate-700">
                                    {num(c.total)}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
            <EstiloInput />
        </>
    );
}

/* ─────────────────────────── compartidos ─────────────────────────── */
function Campo({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}
function EstiloInput() {
    return (
        <style>{`
      .input-fecha {
        border: 1px solid rgb(203 213 225);
        border-radius: 0.5rem;
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
        color: rgb(51 65 85);
        background: white;
        width: 100%;
      }
    `}</style>
    );
}